/* eslint-disable react/no-unstable-nested-components */
import {
  Avatar, Checkbox, FormControlLabel, Tooltip,
} from '@mui/material';
import { createColumnHelper, PaginationState, RowSelectionState } from '@tanstack/react-table';
import React from 'react';
import Table, { TablePaginationState } from 'src/components/Table';
import { EmailIcon } from 'src/icons';
import PhoneIcon from 'src/icons/Phone';
import { DiceCandidateData } from '../../types';

interface DiceCandidatesTableProps {
  fetchingCandidates: boolean;
  pagination: TablePaginationState;
  records: DiceCandidateData[];
  rowSelection: RowSelectionState;
  onChangePagination: (data: PaginationState) => void;
  onRowClick: (row: DiceCandidateData) => void;
  onRowSelectionChange: (data: RowSelectionState) => void;
}

const columnHelper = createColumnHelper<
DiceCandidateData & { sNo?: number, selection?: boolean }
>();

const DiceCandidatesTable = function DiceCandidatesTable(props: DiceCandidatesTableProps) {
  const {
    fetchingCandidates,
    pagination,
    records,
    rowSelection,
    onRowSelectionChange,
    onRowClick,
    onChangePagination,
  } = props;

  const columns = React.useMemo(() => [
    columnHelper.accessor('candidateId', {
      header: ({ table }) => (
        <div className="flex items-center justify-between">
          <div className="ml-4 flex gap-1 items-center">
            <FormControlLabel
              control={(
                <Checkbox
                  indeterminate={table.getIsSomeRowsSelected()}
                  checked={table.getIsAllRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                  size="small"
                />
              )}
              label="Select All"
            />
          </div>
        </div>
      ),
      cell: ({ row }) => {
        const {
          firstName,
          lastName,
          emailAddress,
          phoneNumber,
          candidateLocation,
          status,
          noticePeriod,
          experienceLevel,
          resumeHtmlUrl,
        } = row.original;

        return (
          <div className="flex relative z-0">
            <div className="flex items-center justify-center pl-0 p-2">
              <div className="ml-4">
                <Checkbox
                  indeterminate={row.getIsSomeSelected()}
                  checked={row.getIsSelected()}
                  disabled={!row.getCanSelect()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                  onChange={row.getToggleSelectedHandler()}
                />
              </div>
              <div className="col-span-4 flex justify-center items-start">
                <div className="px-4 items-center justify-center flex flex-col gap-2">
                  <Avatar sx={{ width: 60, height: 60 }}>
                    {firstName?.[0] || ''}
                  </Avatar>
                  <div className="flex items-center gap-2 justify-between">
                    <Tooltip title={emailAddress || 'unknown'}>
                      <EmailIcon className="h-5 w-5" />
                    </Tooltip>
                    <Tooltip title={phoneNumber || 'unknown'}>
                      <PhoneIcon className="h-4 w-4" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div className="gap-1 py-4 flex flex-col">
              <div className="flex gap-2 pb-2">
                <span className="font-bold text-sm">{`${firstName} ${lastName}`}</span>
              </div>
              <div className="grid gap-4 grid-cols-12">
                <div className="flex flex-col gap-2 col-span-6">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-xs text-ellipsis overflow-hidden">Location</span>
                    <span className="text-xs col-span-2 text-ellipsis overflow-hidden">
                      {candidateLocation || 'Not specified'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-xs text-ellipsis overflow-hidden">Status</span>
                    <span className="text-xs col-span-2 text-ellipsis overflow-hidden">
                      {status || 'Not specified'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-xs text-ellipsis overflow-hidden">Notice Period</span>
                    <span className="text-xs col-span-2 text-ellipsis overflow-hidden">
                      {noticePeriod || 'Not specified'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 col-span-6">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-xs text-ellipsis overflow-hidden">Experience Level</span>
                    <span className="text-xs col-span-2 text-ellipsis overflow-hidden">
                      {experienceLevel || 'Not specified'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-xs text-ellipsis overflow-hidden">Resume</span>
                    <span className="text-xs col-span-2 text-ellipsis overflow-hidden">
                      {resumeHtmlUrl ? (
                        <a
                          href={resumeHtmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline"
                        >
                          View
                        </a>
                      ) : (
                        'Not uploaded'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      },
    }),
  ], [rowSelection]);

  return (
    <Table
      loading={fetchingCandidates}
      data={records}
      onRowSelection={onRowSelectionChange}
      rowSelection={rowSelection}
      columns={columns}
      pageSize={20}
      onRowClick={onRowClick}
      manualPagination={pagination}
      onChangePagination={onChangePagination}
    />
  );
};

DiceCandidatesTable.defaultProps = {

};

DiceCandidatesTable.propTypes = {

};

export default DiceCandidatesTable;
